import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from 'react-query';
import { Modal } from 'components/Modal/Modal';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { GraniteInput } from 'components/V2/Input/GraniteInput';
import showToast from 'components/Toast/Toast';
import {
  ModifiedOktaGroupProfile,
  OktaGroupProfile,
} from 'api/companies/schema';
import { updateGroup, createGroup, updatePricingTier } from 'api/companies/api';
import { GraniteSelect } from 'components/Select/Select';
import { usePermissions } from 'hooks/usePermissions';

interface ManageCompanyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  dynamicProps?: {
    groupId: string;
    profile: OktaGroupProfile;
  };
  onCompanySuccess: () => void;
}

const pricingTierOptions = [
  { value: 'Standard', label: 'Standard 0%' },
  { value: 'Highest tier', label: 'Highest tier +15%' },
  { value: 'High tier', label: 'High tier +7%' },
  { value: 'Low tier', label: 'Low tier -7%' },
  { value: 'Lowest tier', label: 'Lowest tier -13%' },
];

export enum pricingTier {
  standard = 'Standard',
  highest_tier = 'Highest tier',
  high_tier = 'High tier',
  low_tier = 'Low tier',
  lowest_tier = 'Lowest tier',
}

const defaultValues: OktaGroupProfile = {
  display_name: '',
  name: '',
  description: 'g360 org',
  macnum: null,
  cw_id: null,
  cw_type: 'company',
  pricing_tier: 'Standard',
};

const specialPricingOptions = [
  { label: 'Wholesale', value: 'wholesale' },
  { label: 'White label', value: 'whitelabel' },
];

export const ManageCompanyDialog: React.FC<ManageCompanyDialogProps> = ({
  isOpen,
  onClose,
  dynamicProps,
  onCompanySuccess,
}) => {
  const isEdit = Boolean(dynamicProps?.groupId);
  const [isUpdating, setIsUpdating] = useState(false);
  const { isSuperAdmin } = usePermissions();
  const queryClient = useQueryClient();
  const {
    handleSubmit,
    register,
    reset,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<OktaGroupProfile>({
    ...(isSuperAdmin
      ? { resolver: zodResolver(ModifiedOktaGroupProfile) }
      : {}),
    defaultValues,
  });
  const pricingTier = watch('pricing_tier');
  useEffect(() => {
    if (!isEdit) {
      reset(defaultValues);
    } else if (dynamicProps?.profile) {
      const pricing_tier: pricingTier =
        (pricingTierOptions.find(
          (option) => option.value === dynamicProps?.profile?.pricing_tier,
        )?.value as pricingTier) ?? 'Standard';
      const specialPricing = [];
      if (pricing_tier === 'Standard') {
        if (dynamicProps.profile.wholesale)
          specialPricing.push({ label: 'Wholesale', value: 'wholesale' });
        if (dynamicProps.profile.whitelabel)
          specialPricing.push({ label: 'White label', value: 'whitelabel' });
      }
      reset({
        ...dynamicProps.profile,
        special_pricing: specialPricing,
        pricing_tier,
      });
    }
  }, [isEdit, reset, dynamicProps]);

  useEffect(() => {
    if (pricingTier !== 'Standard') setValue('special_pricing', []);
  }, [pricingTier, setValue]);

  const mutation = useMutation(
    (payload: OktaGroupProfile) =>
      isEdit
        ? isSuperAdmin
          ? updateGroup(payload, dynamicProps?.groupId as string)
          : updatePricingTier(
              {
                pricing_tier: payload.pricing_tier,
                special_pricing: payload.special_pricing,
              },
              dynamicProps?.groupId as string,
            )
        : createGroup(payload),
    {
      onSuccess: (profile) => {
        showToast.confirmation({
          message: `Company ${isEdit ? 'updated' : 'added'} successfully!`,
        });
        if (!dynamicProps?.groupId) {
          queryClient.invalidateQueries(['companies']);
        } else {
          const queryCache = queryClient.getQueryCache();
          const matchingQueries = queryCache.findAll({
            queryKey: ['companies'],
          });
          for (const { queryKey } of matchingQueries) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            queryClient.setQueryData(queryKey, (data: any) => {
              if (!data) return data;
              return {
                ...data,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                pages: data.pages.map((page: any) => ({
                  ...page,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  data: page.data.map((item: any) =>
                    item.id === dynamicProps.groupId
                      ? { ...item, profile }
                      : item,
                  ),
                })),
              };
            });
          }
        }
        onClose();
        onCompanySuccess();
      },
      onError: ({ response }) => {
        showToast.error({
          message: response.data.message ?? 'Something went wrong',
        });
        setIsUpdating(false);
      },
      onSettled: () => {
        setIsUpdating(false);
      },
    },
  );

  const onSubmit = (payload: OktaGroupProfile) => {
    setIsUpdating(true);
    mutation.mutate(payload);
  };

  const onError = (err: unknown) => {
    console.log('FormError:', err);
    return;
  };

  const onModalClose = () => {
    reset({});
    onClose();
  };

  return (
    <Modal
      isVisible={isOpen}
      close={onModalClose}
      className="w-full max-w-[836px] overflow-hidden rounded-lg"
      enableScrolling={true}
    >
      <div className="flex h-full flex-col">
        <h1 className="bg-background-base-surface-3 px-8 py-4 text-[28px] font-bold leading-9 text-content-base-default">
          {isEdit ? 'Edit company' : 'Add company'}
        </h1>
        <form onSubmit={handleSubmit(onSubmit, onError)} className="p-8">
          <div className="grid grid-cols-2 gap-x-4 gap-y-6 ">
            <Controller
              control={control}
              name="pricing_tier"
              render={({ field }) => {
                const value = pricingTierOptions.find(
                  (option) => option.value === field.value,
                );
                return (
                  <GraniteSelect
                    options={pricingTierOptions}
                    label="Pricing tier"
                    error={errors.pricing_tier?.message}
                    isSearchable={false}
                    {...field}
                    onChange={(e) => field.onChange(e?.value)}
                    value={value}
                  />
                );
              }}
            />
            <Controller
              name="special_pricing"
              control={control}
              render={({ field: { onChange, value, ...field } }) => {
                return (
                  <GraniteSelect
                    {...field}
                    options={specialPricingOptions}
                    onChange={onChange}
                    label="Special pricing (optional)"
                    value={value}
                    error={errors.special_pricing?.message}
                    isMulti
                    isDisabled={pricingTier !== 'Standard'}
                  />
                );
              }}
            />
            {/* <DisplayForPermission onlyForSuperAdmin> */}
            <GraniteInput
              label="Company name"
              placeholder="Company Name"
              {...register('name')}
              error={errors.name?.message}
              disabled={!isSuperAdmin}
            />
            <GraniteInput
              label="Company display name"
              placeholder="Display Name"
              {...register('display_name')}
              error={errors.display_name?.message}
              disabled={!isSuperAdmin}
            />
            <GraniteInput
              label="Company macnum"
              placeholder="Macnum"
              {...register('macnum')}
              error={errors.macnum?.message}
              disabled={!isSuperAdmin}
            />
            <Controller
              control={control}
              name="cw_id"
              render={({ field }) => (
                <GraniteInput
                  label="Connectwise rec id"
                  innerInputClassName="w-full"
                  className="w-full"
                  placeholder="Connectwise rec id"
                  {...field}
                  disabled={!isSuperAdmin}
                  value={`${field.value ?? ''}`}
                  onChange={(e) => {
                    if (e.target.value.length)
                      field.onChange(Number(e.target.value));
                    else field.onChange(null);
                  }}
                  onKeyDown={(e) => {
                    if (!/^[0-9]$/.test(e.key) && e.key !== 'Backspace') {
                      e.preventDefault();
                    }
                  }}
                  error={errors.cw_id?.message}
                />
              )}
            />
            <GraniteInput
              label="Type"
              innerInputClassName="w-full"
              className="w-full"
              placeholder="Type"
              {...register('cw_type')}
              error={errors.cw_type?.message}
              disabled={!isSuperAdmin}
            />
            <GraniteInput
              label="Description"
              placeholder="Description"
              {...register('description')}
              error={errors.description?.message}
              disabled={!isSuperAdmin}
            />
          </div>
          <div className="mt-12 flex gap-4">
            <GraniteButton variant="secondary" onClick={onClose}>
              Cancel
            </GraniteButton>
            <GraniteButton
              variant="primary"
              type="submit"
              size="large"
              disabled={isUpdating}
            >
              Save Changes{' '}
            </GraniteButton>
          </div>
        </form>
      </div>
    </Modal>
  );
};
